import React from "react";
import { motion } from "framer-motion";

import fosLeo from "../../assets/images/EXEpartners/fosLEO.png";
import youthClub from "../../assets/images/EXEpartners/Divisional Federation of Youth Club Ja-Ella.png";

const EXEAbout = () => {
  return (
    <section className="exe-section">
      <div className="exe-container">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="exe-title text-exe-text-accent mb-6">
              What is EXE?
            </h2>
            <div className="h-1 w-24 bg-exe-gradient-primary mx-auto mb-8"></div>
          </motion.div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="exe-heading mb-6">
                The Ultimate Game Development Challenge
              </h3>

              <div className="space-y-6 exe-text">
                <p>
                  EXE is Sri Lanka's premier game development competition organized by{" "}
                  <a 
                    href="https://www.levelstair.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-exe-primary hover:text-exe-text-accent transition-colors font-semibold"
                  >
                    LEVELSTAIR
                  </a>
                  , designed to unite creative minds from schools, universities,
                  and professional studios. Our mission is to elevate the local
                  gaming industry by providing a platform for innovation,
                  collaboration, and recognition.
                </p>

                <p>
                  Whether you're a seasoned developer or just starting your
                  journey, EXE offers categories for every skill level. From 2D
                  indie games to complex 3D Games, we celebrate all forms of
                  interactive entertainment.
                </p>

                <p>
                  Beyond the competition, EXE serves as a bridge between
                  emerging talent and industry leaders, fostering connections
                  that shape the future of game development in Sri Lanka. As part of the{" "}
                  <a 
                    href="https://www.levelstair.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-exe-primary hover:text-exe-text-accent transition-colors font-semibold"
                  >
                    LEVELSTAIR community
                  </a>
                  , participants gain access to ongoing workshops, networking events, and career opportunities.
                </p>
              </div>
            </motion.div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="exe-glass-card p-8 relative overflow-hidden">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-exe-primary rounded-full"></div>
                    <span className="exe-text font-semibold">
                      Innovation-Driven
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-exe-primary rounded-full"></div>
                    <span className="exe-text font-semibold">
                      Industry-Connected
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-exe-primary rounded-full"></div>
                    <span className="exe-text font-semibold">
                      Skill-Building
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-exe-primary rounded-full"></div>
                    <span className="exe-text font-semibold">
                      Community-Focused
                    </span>
                  </div>
                </div>

                {/* Decorative circuit pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-exe-circuit-pattern opacity-10"></div>
              </div>
            </motion.div>
          </div>

          {/* Key Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              {
                title: "Mentorship",
                description:
                  "Learn from industry professionals and experienced developers",
                icon: <ion-icon name="extension-puzzle-outline"></ion-icon>,
              },
              {
                title: "Networking",
                description:
                  "Connect with peers, mentors, and potential employers",
                icon: <ion-icon name="globe-outline"></ion-icon>,
              },
              {
                title: "Recognition",
                description:
                  "Showcase your skills to the wider gaming community",
                icon: <ion-icon name="trophy-outline"></ion-icon>,
              },
              {
                title: "Growth",
                description:
                  "Develop your skills through hands-on competition experience",
                icon: <ion-icon name="analytics-outline"></ion-icon>,
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="exe-card text-center p-6 "
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h4 className="exe-subheading text-lg mb-3 text-exe-text-accent ">
                  {feature.title}
                </h4>
                <p className="exe-text text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Proud Collaborators Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="text-center mb-12">
              <h3 className="exe-heading text-exe-text-accent mb-6">
                Proud Collaborators
              </h3>
              <div className="h-1 w-16 bg-exe-gradient-primary mx-auto mb-8"></div>
            </div>

            <div className="flex justify-center items-center relative">
              {/* Logos */}
              <div className="flex gap-8 items-center flex-wrap justify-center">
                <motion.div 
                  className="h-[150px] w-[200px] flex items-center justify-center p-4 exe-glass-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3,
                    ease: "easeOut",
                    delay: 0.3
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 0 20px rgba(83, 240, 50, 0.3)"
                  }}
                >
                  <img
                    src={fosLeo}
                    alt="University of Colombo Faculty of Science LEO Club"
                    className="object-contain max-h-full max-w-full"
                  />
                </motion.div>

                <motion.div 
                  className="h-[150px] w-[200px] flex items-center justify-center p-4 exe-glass-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3,
                    ease: "easeOut",
                    delay: 0.36
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 0 20px rgba(83, 240, 50, 0.3)"
                  }}
                >
                  <img
                    src={youthClub}
                    alt="Divisional Federation of Youth Club Ja-Ella"
                    className="object-contain max-h-full max-w-full"
                  />
                </motion.div>
              </div>
            </div>

            <div className="text-center mt-8 text-caption text-exe-text-secondary">
              <p>- LEO Club Of University Of Colombo Faculty Of Science -</p>
              <p>- Divisional Federation Of Youth Club Ja-Ella -</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EXEAbout;
